import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { siteInfo } from '@/mocks/siteInfo';
import { useState } from 'react';

const milestones = [
  { year: '2020', event: '凪光在线正式上线，开启在线服务新篇章' },
  { year: '2021', event: '用户突破100万，推出智能推荐系统' },
  { year: '2022', event: '完成多层安全防护体系建设，用户突破300万' },
  { year: '2023', event: '推出多端同步功能，实现全平台无缝覆盖' },
  { year: '2024', event: '荣获行业最佳在线服务奖，用户突破400万' },
  { year: '2025', event: '全面升级AI能力，推出新一代智能服务平台' },
  { year: '2026', event: '用户突破500万，持续引领在线服务行业创新' },
];

const values = [
  {
    icon: 'ri-shield-star-line',
    title: '安全第一',
    description: '我们始终将用户的安全与隐私放在首位，持续投入资源建设最严格的安全防护体系。',
  },
  {
    icon: 'ri-user-heart-line',
    title: '用户至上',
    description: '以用户需求为导向，不断优化产品体验，确保每一次使用都是愉快的旅程。',
  },
  {
    icon: 'ri-lightbulb-flash-line',
    title: '技术创新',
    description: '持续探索前沿技术，将最新科技成果融入产品，为用户创造更大价值。',
  },
  {
    icon: 'ri-team-line',
    title: '诚信共赢',
    description: '坚持公开透明的运营理念，与用户、合作伙伴共同成长，构建良性生态。',
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    const formData = new FormData(form);
    try {
      const res = await fetch('https://readdy.ai/api/form/d9u4tgl3pcjqs2fcsm9g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || '提交失败，请稍后再试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`关于我们 - ${siteInfo.name} | 品牌介绍与联系方式`}
        description={`了解${siteInfo.name}的品牌故事、核心价值观与发展历程。${siteInfo.name}成立于2020年，致力于为用户提供流畅稳定的在线体验。`}
        canonicalUrl="https://naguflow.github.io/about"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: `关于${siteInfo.name}`,
            description: `了解${siteInfo.name}的品牌故事、核心价值观与发展历程。`,
            url: 'https://naguflow.github.io/about',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://naguflow.github.io',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '关于我们',
              },
            ],
          },
        ]}
      />

      <Navbar />

      <main className="pt-20">
        <Breadcrumb items={[{ label: '关于我们' }]} />

        {/* Brand Introduction */}
        <section className="py-10 md:py-20 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4">
                品牌故事
              </span>
              <h1 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-950 mb-6">
                关于 {siteInfo.name}
              </h1>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                {siteInfo.name}成立于{siteInfo.foundingYear}年，是一家专注于在线服务领域的科技平台。
                自创立以来，我们始终秉持「让在线体验更流畅、更安全」的使命，
                致力于为每一位用户提供值得信赖的优质服务。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                经过多年的持续创新与稳健发展，{siteInfo.name}已服务超过{siteInfo.stats.totalUsers}用户，
                凭借卓越的技术实力和优质的服务体验，赢得了广大用户的信赖与好评。
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
              {[
                { value: siteInfo.stats.totalUsers, label: '累计用户' },
                { value: siteInfo.stats.dailyActive, label: '日活跃用户' },
                { value: siteInfo.stats.satisfaction, label: '用户满意度' },
                { value: siteInfo.stats.serviceYears, label: '稳定运营' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-6 bg-background-100 rounded-xl">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <span className="inline-block px-4 py-1.5 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-4">
                核心价值
              </span>
              <h2 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-950 mb-4">
                我们的价值观
              </h2>
              <p className="text-sm text-foreground-600 max-w-2xl mx-auto">
                这些核心价值观指引着我们每一天的工作，也定义了我们与用户之间的关系
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value) => (
                <div
                  key={value.title}
                  className="bg-background-50 rounded-xl p-6 border border-background-200/70 text-center"
                >
                  <div className="w-14 h-14 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-4">
                    <i className={`${value.icon} text-2xl text-accent-600`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2">{value.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <span className="inline-block px-4 py-1.5 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full mb-4">
                发展历程
              </span>
              <h2 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-950 mb-4">
                一路走来
              </h2>
              <p className="text-sm text-foreground-600 max-w-2xl mx-auto">
                从2020年创立至今，我们不断成长与突破
              </p>
            </div>

            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
                {milestones.map((item, index) => (
                  <div
                    key={item.year}
                    className={`relative flex items-start gap-8 mb-8 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-12 md:pl-0`}>
                      <div className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                        <span className="text-sm font-heading font-bold text-primary-600">{item.year}</span>
                        <p className="text-sm text-foreground-700 mt-1">{item.event}</p>
                      </div>
                    </div>
                    <div className="hidden md:block flex-1"></div>
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-primary-500 rounded-full -translate-x-1/2 mt-6 border-2 border-background-50"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-16 md:py-24 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-14">
                <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4">
                  联系我们
                </span>
                <h2 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-950 mb-4">
                  期待与您沟通
                </h2>
                <p className="text-sm text-foreground-600 max-w-2xl mx-auto">
                  无论您有任何问题、建议或合作意向，都欢迎随时与我们联系
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1 space-y-6">
                  <div className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mb-3">
                      <i className="ri-phone-line text-primary-600"></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1">电话咨询</h3>
                    <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
                    <p className="text-xs text-foreground-500 mt-1">周一至周五 9:00-21:00</p>
                  </div>

                  <div className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mb-3">
                      <i className="ri-mail-line text-accent-600"></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1">邮件联系</h3>
                    <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                    <p className="text-xs text-foreground-500 mt-1">工作日24小时内回复</p>
                  </div>

                  <div className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center mb-3">
                      <i className="ri-map-pin-line text-secondary-600"></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1">公司地址</h3>
                    <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                  </div>
                </div>

                <div className="lg:col-span-2 bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <h3 className="text-base font-heading font-semibold text-foreground-950 mb-6">发送消息</h3>
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form=""
                    className="space-y-4"
                  >
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                          姓名 <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                          邮箱 <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        type="text"
                        id="contact-subject"
                        name="subject"
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入消息主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        消息内容 <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={5}
                        maxLength={500}
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您想咨询的内容（最多500字）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      className="px-6 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送消息
                      <i className="ri-send-plane-line ml-2"></i>
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-sm text-accent-600 mt-3">消息已成功发送，我们会尽快与您联系！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 mt-3">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}